import React, { useEffect, useState } from 'react';
import axios from 'axios';

// Requests that run on a timer in the background. They must never make the app
// look busy, otherwise the bar would blink every polling interval.
const SILENT_PATTERNS = [
  /\/api\/notifications(\/|\?|$)/,
  /\/api\/prequalify\/\d+/,
  /\/api\/env(\/|\?|$)/,
  /\/api\/clients\/gemini-usage/,
];

const isSilentUrl = (url) => SILENT_PATTERNS.some((re) => re.test(String(url || '')));

const isSilent = (config) => {
  if (config?.silentActivity) return true;
  return isSilentUrl(config?.url);
};

// Documents are fetched as blobs with the native fetch(), which no axios
// interceptor can see. Only our own API calls are counted.
const isAppRequest = (url) => {
  const str = String(url || '');
  if (!str) return false;
  // blob:/data: reads are local and instant — never worth a progress bar.
  if (/^(blob|data):/i.test(str)) return false;
  if (str.startsWith('/')) return true;
  try {
    const base = process.env.REACT_APP_API_BASE_URL || '';
    if (base && str.startsWith(base)) return true;
    return new URL(str, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
};

// Only show once a request is slow enough to be worth reporting — a spinner
// that flashes for 80ms reads as a glitch.
const SHOW_AFTER_MS = 250;

/**
 * A thin progress bar pinned to the top of the viewport that appears whenever
 * an API request is in flight, so every click that talks to the server shows
 * that something is happening before its result message arrives.
 */
const GlobalActivityIndicator = () => {
  const [active, setActive] = useState(false);

  useEffect(() => {
    let inFlight = 0;
    let showTimer = null;
    let hideTimer = null;

    const sync = () => {
      if (inFlight > 0) {
        if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
        if (!showTimer) {
          showTimer = setTimeout(() => {
            showTimer = null;
            if (inFlight > 0) setActive(true);
          }, SHOW_AFTER_MS);
        }
        return;
      }
      if (showTimer) { clearTimeout(showTimer); showTimer = null; }
      // Let the bar finish its sweep instead of vanishing mid-animation.
      if (!hideTimer) {
        hideTimer = setTimeout(() => { hideTimer = null; setActive(false); }, 220);
      }
    };

    const start = (config) => {
      if (!isSilent(config)) {
        config.__countedActivity = true;
        inFlight += 1;
        sync();
      }
      return config;
    };
    const done = (config) => {
      if (config?.__countedActivity) {
        inFlight = Math.max(0, inFlight - 1);
        sync();
      }
    };

    const reqId = axios.interceptors.request.use(start, (error) => {
      done(error?.config);
      return Promise.reject(error);
    });
    const resId = axios.interceptors.response.use(
      (response) => { done(response?.config); return response; },
      (error) => { done(error?.config); return Promise.reject(error); }
    );

    // Count native fetch() too — document view/download uses it for blobs.
    const originalFetch = window.fetch;
    let patched = null;
    if (typeof originalFetch === 'function') {
      patched = function activityTrackedFetch(input, init) {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const track = isAppRequest(url) && !isSilentUrl(url);
        if (!track) return originalFetch.call(this, input, init);
        inFlight += 1;
        sync();
        let settled = false;
        const release = () => {
          if (settled) return;
          settled = true;
          inFlight = Math.max(0, inFlight - 1);
          sync();
        };
        return originalFetch.call(this, input, init).then(
          (res) => { release(); return res; },
          (err) => { release(); throw err; }
        );
      };
      window.fetch = patched;
    }

    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
      // Only restore if nothing else patched fetch after us.
      if (patched && window.fetch === patched) window.fetch = originalFetch;
      if (showTimer) clearTimeout(showTimer);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, []);

  return (
    <div
      aria-hidden={!active}
      className={`fixed top-0 left-0 right-0 z-[60] h-1.5 pointer-events-none transition-opacity duration-200 ${active ? 'opacity-100' : 'opacity-0'}`}
    >
      <div className="h-full w-full overflow-hidden bg-blue-100/60 dark:bg-slate-700/60">
        <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 shadow-[0_0_8px_rgba(59,130,246,0.6)] animate-[activity-sweep_1.1s_ease-in-out_infinite]" />
      </div>
      <style>{`
        @keyframes activity-sweep {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </div>
  );
};

export default GlobalActivityIndicator;
